import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHeroList, getGwDeckList, getGwCounterList, getGwCounterDetail, deleteGwDeck, deleteGwCounter } from '../../api/senaApi';
import { skillImg as senaSkillImg, heroPortrait, skillLabel } from './senaAssets';
import './SenaGuildWar.css';

const skillImg = (heroNm, skillNo) => senaSkillImg(heroNm, 'ALLY', skillNo);

export default function SenaGuildWar() {
  const navigate = useNavigate();

  // 영웅 풀 (검색 자동완성용)
  const [allyHeroes, setAllyHeroes] = useState([]);

  // 적군 덱
  const [deckSearch, setDeckSearch] = useState('');
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [deckLoading, setDeckLoading] = useState(false);

  // 카운터 덱
  const [counters, setCounters] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState(null);
  const [counterDetail, setCounterDetail] = useState(null);
  const [counterLoading, setCounterLoading] = useState(false);

  // 영웅 로드
  useEffect(() => {
    getHeroList('ALLY').then(r => setAllyHeroes(r.data || [])).catch(() => {});
  }, []);

  // 덱 검색
  const handleSearchDeck = () => {
    setDeckLoading(true);
    setSelectedDeck(null);
    setCounters([]);
    setSelectedCounter(null);
    setCounterDetail(null);
    getGwDeckList(deckSearch)
      .then(r => setDecks(r.data || []))
      .catch(() => setDecks([]))
      .finally(() => setDeckLoading(false));
  };

  useEffect(() => { handleSearchDeck(); }, []);

  // 덱 선택 → 카운터 목록 로드
  const handleSelectDeck = (deck) => {
    setSelectedDeck(deck);
    setSelectedCounter(null);
    setCounterDetail(null);
    setCounterLoading(true);
    getGwCounterList(deck.deckId)
      .then(r => setCounters(r.data || []))
      .catch(() => setCounters([]))
      .finally(() => setCounterLoading(false));
  };

  // 카운터 선택 → 스킬 케이스 로드
  const handleSelectCounter = (counter) => {
    setSelectedCounter(counter);
    getGwCounterDetail(counter.counterId)
      .then(r => setCounterDetail(r.data))
      .catch(() => setCounterDetail(null));
  };

  // 스킬ID → 이름 매핑 헬퍼
  const findSkillInfo = (skillIdStr) => {
    const skillId = Number(skillIdStr);
    for (const hero of allyHeroes) {
      const sk = (hero.skills || []).find(s => s.skillId === skillId);
      if (sk) return { heroNm: hero.heroNm, skillNo: sk.skillNo, skillNm: sk.skillNm };
    }
    return { heroNm: '?', skillNo: 0, skillNm: '?' };
  };

  const renderSkillChip = (skillIdStr, order) => {
    const info = findSkillInfo(skillIdStr);
    const img = skillImg(info.heroNm, info.skillNo);
    return (
      <div key={`${skillIdStr}-${order}`} className="gw-skill-chip ally">
        <span className="gw-skill-chip-order">{order}</span>
        {img ? <img className="gw-skill-chip-img" src={img} alt="" /> : null}
        <span className="gw-skill-chip-text">{info.heroNm} {skillLabel(info.skillNo)}</span>
      </div>
    );
  };

  // 카운터 영웅별 비고 (세팅 메모)
  const counterNotes = counterDetail ? [
    { heroNm: counterDetail.hero1Nm, note: counterDetail.note1 },
    { heroNm: counterDetail.hero2Nm, note: counterDetail.note2 },
    { heroNm: counterDetail.hero3Nm, note: counterDetail.note3 },
  ] : [];

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate('/sena')}>← 메인으로</button>
        <h1 className="sena-header-guild">길드전(공격)</h1>
        <p className="sena-header-sub">카운터 덱 & 스킬 공략</p>
      </header>

      <div className="gw-war-body">
        {/* ===== 적군 공간 ===== */}
        <section className="gw-war-panel">
          <div className="gw-war-panel-header">
            <h2 className="gw-war-panel-title">적팀</h2>
            <div className="gw-war-search">
              <input
                className="gw-war-search-input"
                placeholder="영웅 이름 검색..."
                value={deckSearch}
                onChange={e => setDeckSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchDeck()}
              />
              <button className="gw-war-search-btn" onClick={handleSearchDeck}>검색</button>
            </div>
            <button className="gw-war-write-btn" onClick={() => navigate('/sena/gw/write')}>덱 등록</button>
          </div>

          <div className="gw-war-panel-body">
            {deckLoading ? <p className="gw-war-empty">조회 중...</p> :
             decks.length === 0 ? <p className="gw-war-empty">등록된 적군 덱이 없습니다.</p> : (
              <div className="gw-war-deck-list">
                {decks.map(deck => (
                  <button
                    key={deck.deckId}
                    className={`gw-war-deck-card ${selectedDeck?.deckId === deck.deckId ? 'active' : ''}`}
                    onClick={() => handleSelectDeck(deck)}
                  >
                    <div className="gw-war-deck-heroes">
                      <span className="gw-war-hero-tag">{deck.hero1Nm}</span>
                      <span className="gw-war-hero-tag">{deck.hero2Nm}</span>
                      <span className="gw-war-hero-tag">{deck.hero3Nm}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="gw-war-deck-author">{deck.authorDisplayNm || deck.authorNm}</span>
                      {(localStorage.getItem('userId') === deck.authorNm || localStorage.getItem('userRole') === 'ADMIN') && (
                        <button className="gw-war-del-btn" onClick={(e) => {
                          e.stopPropagation();
                          if (!window.confirm('적군 덱을 삭제하시겠습니까? (카운터도 함께 삭제됩니다)')) return;
                          deleteGwDeck(deck.deckId).then(() => {
                            setDecks(prev => prev.filter(d => d.deckId !== deck.deckId));
                            if (selectedDeck?.deckId === deck.deckId) { setSelectedDeck(null); setCounters([]); setCounterDetail(null); }
                          }).catch(() => alert('삭제 실패'));
                        }}>삭제</button>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ===== 우리팀 공간 ===== */}
        <section className="gw-war-panel">
          <div className="gw-war-panel-header">
            <h2 className="gw-war-panel-title">우리팀</h2>
            {selectedDeck && (
              <button className="gw-war-write-btn" onClick={() => navigate(`/sena/gw/counter/write?deckId=${selectedDeck.deckId}`)}>
                카운터 등록
              </button>
            )}
          </div>

          <div className="gw-war-panel-body">
            {!selectedDeck ? (
              <p className="gw-war-empty">좌측에서 적군 덱을 선택하세요.</p>
            ) : counterLoading ? (
              <p className="gw-war-empty">카운터 조회 중...</p>
            ) : counters.length === 0 ? (
              <p className="gw-war-empty">등록된 카운터가 없습니다.</p>
            ) : (
              <div className="gw-war-counter-list">
                {counters.map(counter => (
                  <button
                    key={counter.counterId}
                    className={`gw-war-deck-card ${selectedCounter?.counterId === counter.counterId ? 'active' : ''}`}
                    onClick={() => handleSelectCounter(counter)}
                  >
                    <div className="gw-war-deck-heroes">
                      <span className="gw-war-hero-tag ally">{counter.hero1Nm}</span>
                      <span className="gw-war-hero-tag ally">{counter.hero2Nm}</span>
                      <span className="gw-war-hero-tag ally">{counter.hero3Nm}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="gw-war-deck-author">{counter.authorDisplayNm || counter.authorNm}</span>
                      {(localStorage.getItem('userId') === counter.authorNm || localStorage.getItem('userRole') === 'ADMIN') && (
                        <>
                          <button className="gw-war-write-btn" style={{ height: 28, fontSize: 11, padding: '0 10px' }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/sena/gw/counter/write?counterId=${counter.counterId}`); }}>
                            스킬 수정
                          </button>
                          <button className="gw-war-del-btn" onClick={(e) => {
                            e.stopPropagation();
                            if (!window.confirm('카운터 덱을 삭제하시겠습니까?')) return;
                            deleteGwCounter(counter.counterId).then(() => {
                              setCounters(prev => prev.filter(c => c.counterId !== counter.counterId));
                              if (selectedCounter?.counterId === counter.counterId) { setSelectedCounter(null); setCounterDetail(null); }
                            }).catch(() => alert('삭제 실패'));
                          }}>삭제</button>
                        </>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ===== 스킬 경우의수 ===== */}
      {counterDetail && (
        <div className="gw-war-skill-section">
          <h2 className="gw-war-skill-title">
            스킬 경우의수 — {selectedDeck?.hero1Nm}/{selectedDeck?.hero2Nm}/{selectedDeck?.hero3Nm}
            &nbsp;vs&nbsp;
            {counterDetail.hero1Nm}/{counterDetail.hero2Nm}/{counterDetail.hero3Nm}
          </h2>

          {counterDetail.description && (
            <p className="gw-war-skill-desc">{counterDetail.description}</p>
          )}

          {/* 영웅별 세팅 비고 — 비어 있어도 3명 모두 표시 (저장 여부를 화면에서 알 수 있게) */}
          <div className="gw-war-case-group">
            <h3 className="gw-war-case-group-title">영웅 세팅</h3>
            <div className="gw-war-note-list">
              {counterNotes.map((n, i) => (
                <div key={i} className="gw-war-note-row">
                  <span className="gw-war-note-hero">
                    {heroPortrait(n.heroNm, 'ALLY') && (
                      <img className="gw-war-note-portrait" src={heroPortrait(n.heroNm, 'ALLY')} alt="" />
                    )}
                    {n.heroNm}
                  </span>
                  {n.note
                    ? <span className="gw-war-note-text">{n.note}</span>
                    : <span className="gw-war-note-text empty">— 비고 없음</span>}
                </div>
              ))}
            </div>
          </div>

          {/* 스킬 케이스 (적 스킬은 모른다고 보고 아군 스킬 순서만 기록) */}
          {(counterDetail.skillCases || []).length > 0 && (
            <div className="gw-war-case-group">
              <h3 className="gw-war-case-group-title">스킬 순서</h3>
              {(counterDetail.skillCases || [])
                .map(sc => (
                  <div key={sc.caseId} className="gw-war-case-row">
                    <div className="gw-war-case-side">
                      <span className="gw-war-case-label">아군</span>
                      <div className="gw-war-case-skills">
                        {(sc.allySkills || '').split(',').filter(Boolean).map((id, i) => renderSkillChip(id, i + 1))}
                      </div>
                    </div>
                    {sc.description && <p className="gw-war-case-desc">{sc.description}</p>}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
